<!-- =========================================================================================
    File Name: ShortcutCard.vue
    Description: Shortcut card component
    ----------------------------------------------------------------------------------------
    Item Name: ShortcutCard
    Author: Janaka Edirisinghe
========================================================================================== -->

<template>
  <vx-card class="overflow-hidden">
    <div slot="no-body">
      <div
        class="p-6"
        :class="{
          'flex justify-between flex-row-reverse items-center': iconRight,
          'text-center': !iconRight && hideChart,
          'pb-0': !hideChart,
        }"
      >
        <span
          v-if="iconText"
          class="inline-flex items-center justify-center rounded-full font-bold"
          :class="[{ 'mb-4': !iconRight }]"
          :style="{ background: `${rgb_color}`, color: `${hex_color}`, width: '3rem', height: '3rem', fontSize: '1.5rem' }"
        ><span :style="{ marginLeft: iconText === '€' ? '-2px' : '0' }">{{ iconText }}</span></span>
        <feather-icon
          v-else
          :icon="icon"
          class="p-3 inline-flex rounded-full"
          :class="[{ 'mb-4': !iconRight }]"
          :style="{ background: `${rgb_color}`, color: `${hex_color}` }"
        ></feather-icon>
        <div class="truncate">
          <h2 class="mb-1 font-bold">
            {{ statistic }}
            <small v-if="statisticSuffix" class="font-normal text-sm opacity-75">{{ statisticSuffix }}</small>
          </h2>
          <span class="relative">
            {{ statisticTitle }}
            <span v-if="showDatePicker" class="cursor-pointer align-middle ml-1" @click="openDatePicker">
              <feather-icon
                icon="CalendarIcon"
                svgClasses="h-6 w-6"
                :style="{ color: `${hex_color}` }"
              ></feather-icon>
            </span>
            <flat-pickr
              v-if="showDatePicker"
              ref="datePicker"
              v-model="localDate"
              :config="{ dateFormat: 'd/m/Y' }"
              style="position: absolute; width: 0; height: 0; padding: 0; border: 0; opacity: 0; pointer-events: none;"
            />
          </span>
        </div>
      </div>
    </div>
    <div
      slot="no-body"
      v-if="shortCutData && shortCutData.length"
      class="flex justify-center flex-wrap py-4"
    >
      <div
        v-for="(item, index) in shortCutData"
        :key="index"
        class="px-4"
        style="flex: 0 0 125px"
      >
        <div class="flex flex-col items-center">
          <div class="mb-2 text-sm whitespace-nowrap">
            {{ item.name }}
          </div>
          <div class="text-primary font-bold">
            {{
              item.shortCutValue
                ? `${item.shortCutValue.currency ? item.shortCutValue.currency + " " : ""} ${item.shortCutValue.value}`
                : "-"
            }}
          </div>

          <div
            class="text-center whitespace-nowrap"
            :style="{ color: hex_color, fontSize: '22px' }"
          ></div>
        </div>
      </div>
    </div>
  </vx-card>
</template>

<script>
import _color from "@/assets/utils/color.js";
import { RepositoryFactory } from "@/repository/RepositoryFactory";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";

const StaffRepository = RepositoryFactory.get("staff");
const ShortcutsRepository = RepositoryFactory.get("shortcuts");

export default {
  components: {
    flatPickr,
  },
  props: {
    icon: {
      type: String,
      default: "",
    },
    iconText: {
      type: String,
      default: "",
    },
    statistic: {
      type: [Number, String],
      required: true,
    },
    statisticTitle: {
      type: String,
    },
    statisticSuffix: {
      type: String,
      default: "",
    },
    chartData: {
      // type: Array,
      // required: true
    },
    color: {
      type: String,
      default: "#39b24b",
    },
    colorTo: {
      type: String,
    },
    // chartType: {
    //     type: String,
    //     default: 'line-chart',
    // },
    type: {
      type: String,
      default: "line",
    },
    iconRight: {
      type: Boolean,
      default: false,
    },
    hideChart: {
      type: Boolean,
      default: false,
    },
    shortCutData: [],
    showDatePicker: {
      type: Boolean,
      default: false,
    },
    date: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      currencies: [],
      isLoadingShortcuts: false,
    };
  },
  async mounted() {
    await this.fetchCurrencies();
    this.loadShortcutDataAsync();
  },
  methods: {
    onClickShortcut() {
      this.$router.push(`${this.shortcut.url}`).catch(() => {});
    },
    openDatePicker() {
      if (this.$refs.datePicker && this.$refs.datePicker.fp) {
        this.$refs.datePicker.fp.open();
      }
    },
    loadShortcutDataAsync() {
      setTimeout(async () => {
        if (!this.shortCutData || this.shortCutData.length === 0) {
          return;
        }

        try {
          await Promise.all(
            this.shortCutData.map(async (element) => {
              try {
                if (element.endpoint) {
                  const data = await this.fetchShortcutData(element);
                  this.$set(element, "shortCutValue", data);
                }
              } catch (error) {
                console.error("Error fetching shortcut:", error);
                this.$set(element, "shortCutValue", [{ value: "-" }]);
              }
            })
          );
        } catch (e) {
        } finally {
          this.isLoadingShortcuts = false;
        }
      }, 0);
    },
    async fetchShortcutData(item) {
      try {
        this.formatted_shortcut_list = [];
        const { data } = (
          await ShortcutsRepository.getShortCutData(item.endpoint)
        ).data;
        return data;
      } catch (error) {
        // this.convertAndNotifyError(error);
      }
    },
    async fetchCurrencies() {
      this.$vs.loading();
      const { data } = (await StaffRepository.getCurrencyList()).data;
      this.currencies = data;
      this.$vs.loading.close();
    },
  },
  computed: {
    rgb_color: function () {
      if (_color.isHexColor(this.color)) {
        return _color.getColor(this.color, 0.15);
      } else {
        return _color.getColor("#39b24b", 0.15);
      }
    },
    hex_color: function () {
      if (_color.isHexColor(this.color)) {
        return this.color;
      } else {
        return "#39b24b";
      }
    },
    localDate: {
      get() {
        return this.date;
      },
      set(value) {
        this.$emit("date-change", value);
      },
    },
  },
};
</script>
